// Kalender (README §5.7, Screen 40) – Einträge je Tag, Filter nach Art, Fristen als virtuelle Einträge.

import type { FristEintrag } from '../kalender/fristen';
import { terminKategorie, terminTag, type TerminKategorie } from './termine';

export type KalenderFilter = 'alle' | TerminKategorie;

export interface KalenderTerminRoh {
  id: string;
  titel: string | null;
  typ: string | null;
  typ_freitext?: string | null;
  start_datum: string;
  end_datum?: string | null;
  ganztaegig?: boolean | null;
  status?: string | null;
  objekt_id?: string | null;
}

/** Lage eines Tages in einem mehrtägigen Termin (für den durchgehenden Balken im Raster). */
export interface TerminSpanne { erster: boolean; letzter: boolean; tage: number }

export type KalenderEintrag<T extends KalenderTerminRoh> =
  | { art: 'termin'; id: string; tag: string; kat: TerminKategorie; termin: T; spanne?: TerminSpanne }
  | { art: 'frist'; id: string; tag: string; frist: FristEintrag };

const MAX_TAGE = 62;

function plusTag(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * Alle Tage (YYYY-MM-DD), die ein Termin belegt (#615: Urlaub an jedem Tag zeigen, wie Outlook).
 * Endet ein Termin genau um Mitternacht, zählt dieser Tag nicht mehr mit — so liefert Outlook
 * ganztägige Termine (Ende = 0 Uhr des Folgetags). Die App selbst speichert 23:59:59 des letzten Tages.
 * Höchstens 62 Tage, damit ein verirrtes Enddatum den Kalender nicht flutet.
 */
export function terminTage(start: string, ende: string | null | undefined, ganztaegig?: boolean | null): string[] {
  const erster = terminTag(start, ganztaegig);
  if (!ende) return [erster];
  let letzter = terminTag(ende, ganztaegig);
  const zeit = ganztaegig || /^\d{4}-\d{2}-\d{2}$/.test(ende) ? ende.slice(11, 19) : (() => {
    const d = new Date(ende);
    return Number.isNaN(d.getTime()) ? '' : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  })();
  if (letzter > erster && (zeit === '' || zeit === '00:00:00') && ende.length > 10) letzter = plusTag(letzter, -1);
  if (letzter <= erster) return [erster];
  const tage: string[] = [];
  for (let tag = erster; tag <= letzter && tage.length < MAX_TAGE; tag = plusTag(tag, 1)) tage.push(tag);
  return tage;
}

/**
 * Einträge je Tag (YYYY-MM-DD): Termine nach Filter, Fristen nur wenn eingeschaltet. Mehrtägige Termine
 * stehen an jedem ihrer Tage und zuerst (damit der Balken über die Tage in derselben Zeile bleibt),
 * danach nach Uhrzeit, Fristen zuletzt.
 */
export function eintraegeJeTag<T extends KalenderTerminRoh>(
  termine: readonly T[], fristen: readonly FristEintrag[], filter: KalenderFilter, mitFristen: boolean,
): Map<string, KalenderEintrag<T>[]> {
  const m = new Map<string, KalenderEintrag<T>[]>();
  const dazu = (tag: string, e: KalenderEintrag<T>) => m.set(tag, [...(m.get(tag) ?? []), e]);
  const mitTagen = termine.map(t => ({ t, tage: terminTage(t.start_datum, t.end_datum, t.ganztaegig) }));
  mitTagen
    .sort((a, b) => Number(b.tage.length > 1) - Number(a.tage.length > 1) || a.t.start_datum.localeCompare(b.t.start_datum) || a.t.id.localeCompare(b.t.id))
    .forEach(({ t, tage }) => {
      const kat = terminKategorie(t);
      if (filter !== 'alle' && kat !== filter) return;
      tage.forEach((tag, i) => dazu(tag, {
        art: 'termin', id: t.id, tag, kat, termin: t,
        ...(tage.length > 1 ? { spanne: { erster: i === 0, letzter: i === tage.length - 1, tage: tage.length } } : {}),
      }));
    });
  if (mitFristen) fristen.forEach(f => dazu(f.datum, { art: 'frist', id: f.id, tag: f.datum, frist: f }));
  return m;
}

/** Anzahl je Filter (für die Chips), Fristen zählen nicht mit. */
export function kategorieAnzahl(termine: readonly KalenderTerminRoh[]): Record<KalenderFilter, number> {
  const z: Record<KalenderFilter, number> = { alle: termine.length, wartung: 0, vermietung: 0, versammlung: 0, ablesung: 0, sonstige: 0 };
  termine.forEach(t => { z[terminKategorie(t)] += 1; });
  return z;
}

/** „Alle Termine aus 8 Objekten · 34 Termine“ */
export function kalenderUntertitel(termine: readonly KalenderTerminRoh[]): string {
  const objekte = new Set(termine.map(t => t.objekt_id).filter(Boolean)).size;
  return `Alle Termine aus ${objekte} ${objekte === 1 ? 'Objekt' : 'Objekten'} · ${termine.length} ${termine.length === 1 ? 'Termin' : 'Termine'}`;
}

/** Monatsgrenzen für die Abfrage: [erster Tag, erster Tag des Folgemonats). */
export function monatsGrenzen(jahr: number, monat: number): { von: string; bis: string } {
  const p = (j: number, m: number) => `${j}-${String(m).padStart(2, '0')}-01`;
  return { von: p(jahr, monat), bis: monat === 12 ? p(jahr + 1, 1) : p(jahr, monat + 1) };
}
